import { useEffect, useRef, useState } from 'react';
import {
  ConversationChannel,
  SenderAvailabilityResponse,
} from 'src/api/generated';
import { useAlert } from 'src/contexts/alert-context';
import { useSupportTenant } from 'src/modules/shared/contexts/support-tenant-context';
import { useCurrentUser } from 'src/modules/shared/hooks/api/admin/users';
import { useSenderAvailability } from 'src/modules/shared/hooks/api/messaging/sender-availability';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { useMediaQuery } from 'src/modules/shared/hooks/use-media-query';
import { standardizePhoneNumber } from 'src/modules/shared/utils/formatting/phone.util';
import { getWhatsAppSendErrorMessage } from 'src/modules/shared/utils/integrations/whatsapp-send-error.util';
import { useWhatsAppTemplates } from './api/templates';
import { usePhoneNumberInput, useSelectedPhone } from './phones';
import { useTemplateComposerDraft } from './use-template-composer-draft';
import type { WhatsAppTemplateModalContentProps } from '../types/templates/template-modal.types';
import {
  buildTemplateComposerComponents,
  describeTemplate,
  isTemplateFieldValid,
} from '../utils/template-composer.util';

/**
 * Coordinates sender evidence, scoped drafts and the consumer-owned send lifecycle.
 * @param props - Modal context and the existing messaging mutation action.
 * @returns Display state and guarded actions; provider definitions remain query-owned.
 */
export const useWhatsAppTemplateComposer = ({
  isOpen,
  onClose,
  fromPhoneNumberId,
  initialPhoneAddress,
  toNumber,
  onSendTemplate,
  isSending,
}: WhatsAppTemplateModalContentProps) => {
  const { t } = useI18n();
  const { showAlert } = useAlert();
  const { assumedTenantId } = useSupportTenant();
  const { user } = useCurrentUser();
  const isDesktop = useMediaQuery('(min-width: 640px)');
  const [mobileView, setMobileView] = useState<'personalize' | 'preview'>(
    'personalize'
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submissionLock = useRef(false);
  const [sendError, setSendError] = useState<{
    key: string;
    message: string;
  }>();
  const recipient = usePhoneNumberInput({ initialNumber: toNumber ?? '' });
  const selectedToNumber = toNumber ?? recipient.phoneNumber;
  const isNumberValid = Boolean(standardizePhoneNumber(selectedToNumber));
  const {
    phones,
    loading: phonesLoading,
    error: phonesError,
    selectedPhoneId,
    selectedPhone,
    handlePhoneChange,
  } = useSelectedPhone({
    initialPhoneId: fromPhoneNumberId,
    initialPhoneAddress,
  });
  const {
    data: availability,
    loading: availabilityLoading,
    error: availabilityError,
  } = useSenderAvailability(
    {
      channel: ConversationChannel.WHATSAPP,
      senderId: selectedPhone?.providerId ?? '',
    },
    isOpen
  );
  const connectionBlocked =
    availabilityLoading ||
    Boolean(availabilityError) ||
    availability?.state !== SenderAvailabilityResponse.state.AVAILABLE;
  const {
    templates,
    loading: templatesLoading,
    error: templatesError,
    refetch,
  } = useWhatsAppTemplates({
    integrationProviderId: selectedPhone?.integrationProviderId,
    tenantId: assumedTenantId ?? undefined,
  });
  const scope = JSON.stringify([
    assumedTenantId ?? user?.tenantId,
    selectedPhone?.integrationProviderId,
    selectedPhoneId,
    standardizePhoneNumber(selectedToNumber) ?? selectedToNumber,
  ]);
  const {
    selectedTemplate,
    values,
    selectTemplate,
    resetSelection,
    updateValue,
    clearSubmittedDraft,
    draftKey,
  } = useTemplateComposerDraft({ scope, templates });
  useEffect(() => {
    if (isOpen) return;
    resetSelection();
    setMobileView('personalize');
    setSendError(undefined);
  }, [isOpen, resetSelection]);
  const definition = selectedTemplate
    ? describeTemplate(selectedTemplate)
    : undefined;
  const missingCount =
    definition?.fields.filter(
      (field) => !isTemplateFieldValid(field, values[field.key])
    ).length ?? 0;
  const pending = isSending || isSubmitting;
  const canSend =
    !pending &&
    !connectionBlocked &&
    !phonesLoading &&
    !phonesError &&
    Boolean(selectedPhone) &&
    !templatesLoading &&
    !templatesError &&
    Boolean(selectedTemplate) &&
    Boolean(definition) &&
    !definition?.unavailableReason &&
    missingCount === 0 &&
    isNumberValid;
  const chooseTemplate = (id?: string) => {
    selectTemplate(id);
    setMobileView('personalize');
  };
  const close = () => {
    if (!pending && !submissionLock.current) onClose();
  };
  const handleSend = async () => {
    if (
      !canSend ||
      submissionLock.current ||
      !selectedPhone ||
      !selectedTemplate ||
      !definition
    ) {
      return;
    }
    submissionLock.current = true;
    setIsSubmitting(true);
    setSendError(undefined);
    try {
      await onSendTemplate({
        fromPhoneNumber: selectedPhone.address,
        toPhoneNumber: selectedToNumber,
        template: selectedTemplate,
        components: buildTemplateComposerComponents(definition, values),
      });
      clearSubmittedDraft();
      setMobileView('personalize');
      showAlert(t('whatsappTemplates.sent'), 'success');
      onClose();
    } catch (error) {
      setSendError({
        key: draftKey,
        message: getWhatsAppSendErrorMessage(
          error,
          t('communications.messageBox.whatsapp.sendError'),
          t('communications.messageBox.whatsapp.connectionUnavailable')
        ),
      });
    } finally {
      submissionLock.current = false;
      setIsSubmitting(false);
    }
  };
  const variants = templates.filter(
    (template) => template.name === selectedTemplate?.name
  );
  const completeFieldsLabel =
    canSend || pending
      ? t('whatsappTemplates.ready')
      : t('whatsappTemplates.checkRequirements');
  const readiness = !selectedTemplate
    ? t('whatsappTemplates.selectionRequired')
    : definition?.unavailableReason
      ? t(`whatsappTemplates.unavailable.${definition.unavailableReason}`)
      : missingCount
        ? t('whatsappTemplates.remaining', { count: missingCount })
        : completeFieldsLabel;

  return {
    isDesktop,
    mobileView,
    setMobileView,
    selectedToNumber,
    recipient,
    isNumberValid,
    phones,
    phonesLoading,
    phonesError,
    selectedPhoneId,
    selectedPhone,
    handlePhoneChange,
    availabilityLoading,
    availabilityError,
    connectionBlocked,
    templates,
    templatesLoading,
    templatesError,
    refetch,
    selectedTemplate,
    values,
    updateValue,
    draftKey,
    definition,
    pending,
    canSend,
    chooseTemplate,
    close,
    handleSend,
    variants,
    readiness,
    sendError,
  };
};
